import { Geotab, StatusData } from "@fairfleet/geotab";

const DEFAULT_RESULTS_LIMIT = 50_000;

/** One series to fetch: a device, optionally narrowed to a single diagnostic. */
export interface StatusDataFetchTask {
  /** Identifier the caller uses to route each page back to the right series. */
  key: string;
  deviceId: string;
  /** Omit to fetch all diagnostics of the device. */
  diagnosticId?: string;
}

export interface FetchStatusDataCompleteOptions {
  tasks: StatusDataFetchTask[];
  fromDate: Date;
  toDate: Date;
  /** Called with each page as it arrives, so callers can render partial results. */
  onPage: (key: string, page: StatusData[]) => void;
  resultsLimit?: number;
  signal?: AbortSignal;
}

/**
 * Fetches all `StatusData` for a set of series within `[fromDate, toDate]`.
 *
 * A single `Get` per series is not enough. Geotab returns records oldest first and silently
 * truncates at `resultsLimit`, so a busy device or a frequently reported diagnostic loses the
 * newest — and usually most interesting — part of the window. Whenever a page comes back full,
 * the series is fetched again starting just after its newest record, until the window is covered.
 *
 * Every series is requested concurrently. The client queues `Get` calls and flushes them as
 * `ExecuteMultiCall` batches, so this issues one HTTP request per hundred series, not per series.
 *
 * @param geotabClient - The Geotab client to call through.
 * @param options - See {@link FetchStatusDataCompleteOptions}.
 */
export async function fetchStatusDataComplete(
  geotabClient: Geotab,
  options: FetchStatusDataCompleteOptions,
): Promise<void> {
  const { tasks, fromDate, toDate, onPage, signal } = options;
  const resultsLimit = options.resultsLimit ?? DEFAULT_RESULTS_LIMIT;

  signal?.throwIfAborted();

  // One failed series aborts the rest. Without this the remaining series keep paging after the
  // caller has already received the rejection, spending requests on a result nobody will read.
  const controller = new AbortController();
  const abortFromCaller = () => controller.abort(signal?.reason);
  signal?.addEventListener("abort", abortFromCaller, { once: true });

  try {
    await Promise.all(
      tasks.map(task =>
        fetchSeries(geotabClient, task, fromDate, toDate, resultsLimit, onPage, controller.signal).catch(
          (error: unknown) => {
            controller.abort(error);
            throw error;
          },
        ),
      ),
    );
  } finally {
    signal?.removeEventListener("abort", abortFromCaller);
  }
}

async function fetchSeries(
  geotabClient: Geotab,
  task: StatusDataFetchTask,
  fromDate: Date,
  toDate: Date,
  resultsLimit: number,
  onPage: FetchStatusDataCompleteOptions["onPage"],
  signal: AbortSignal,
): Promise<void> {
  let pageFromDate = fromDate;

  while (true) {
    signal.throwIfAborted();

    const response = await geotabClient.get(
      "StatusData",
      {
        deviceSearch: { id: task.deviceId },
        ...(task.diagnosticId ? { diagnosticSearch: { id: task.diagnosticId } } : {}),
        fromDate: pageFromDate,
        toDate,
      },
      resultsLimit,
      signal,
    );
    if (!Array.isArray(response)) {
      throw new Error(
        `Unexpected StatusData response for ${describeSeries(task)}: expected an array, got ${String(response)}`,
      );
    }
    const page = response;
    onPage(task.key, page);

    if (page.length < resultsLimit) {
      return;
    }

    // The page was truncated, so continue just past its newest record. Records sharing that
    // exact millisecond beyond the limit are the one case this cannot recover; a page of
    // 50 000 records inside a single millisecond does not occur in practice.
    const newestMs = page.reduce((max, record) => {
      // A single unparseable timestamp must not poison the fold — with NaN in it, paging
      // would stop and the newest part of the window would go missing silently, which is
      // the very failure this module exists to prevent.
      const recordMs = new Date(record.dateTime).getTime();
      return Number.isFinite(recordMs) ? Math.max(max, recordMs) : max;
    }, -Infinity);

    if (!Number.isFinite(newestMs)) {
      throw new Error(
        `Cannot page: no usable dateTime in a full page of StatusData for ${describeSeries(task)}`,
      );
    }

    const nextFromDate = new Date(newestMs + 1);
    if (nextFromDate.getTime() <= pageFromDate.getTime()) {
      throw new Error(
        `Cannot page: no progress after a full page of StatusData for ${describeSeries(task)} (newest record ${new Date(newestMs).toISOString()} is not after the requested fromDate ${pageFromDate.toISOString()})`,
      );
    }
    // `toDate` is inclusive, so a cursor that lands exactly on it still has a page to fetch.
    if (nextFromDate.getTime() > toDate.getTime()) {
      return;
    }
    pageFromDate = nextFromDate;
  }
}

function describeSeries(task: StatusDataFetchTask): string {
  return task.diagnosticId
    ? `device ${task.deviceId} / diagnostic ${task.diagnosticId}`
    : `device ${task.deviceId}`;
}
